// features/item-filter/SupplierFilter.jsx
//
// The filter bar's supplier dropdown: "All suppliers" plus every supplier of
// the store (`useSuppliers`, already in name order). Mounted by ItemFilterBar
// only on a page that passes `supplierFilter`, so no other page fetches the
// list.
//
// The selection is the picked supplier's `{ supplier_id, supplier_name }`, not
// its id alone: a filter restored before the list arrives, or kept after the
// read failed, still shows the name of what the grid is filtered to.

import { useEffect } from 'react';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSuppliers } from '../../hooks/useReferenceData';

const ALL = 'all';

export default function SupplierFilter({ selected, onChange }) {
  const { t } = useTranslation();
  const label = t('searchFilters.supplierLabel');
  const { data: suppliers, loading, error } = useSuppliers();

  // The message says only "couldn't load"; the log carries the cause.
  useEffect(() => {
    if (error) console.error('Suppliers load failed', error);
  }, [error]);

  const listed = selected !== null
    && suppliers.some((s) => s.supplier_id === selected.supplier_id);

  const handleChange = (e) => {
    const id = e.target.value;
    if (id === ALL) {
      onChange(null);
      return;
    }
    const { supplier_id, supplier_name } = suppliers.find((s) => s.supplier_id === id);
    onChange({ supplier_id, supplier_name });
  };

  return (
    <FormControl size="small" className="filter-row-slot" error={error !== null}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={selected?.supplier_id ?? ALL}
        onChange={handleChange}
        label={label}
        disabled={loading || error !== null}
      >
        <MenuItem value={ALL}>{t('searchFilters.supplierAllLabel')}</MenuItem>
        {selected !== null && !listed && (
          <MenuItem value={selected.supplier_id}>{selected.supplier_name}</MenuItem>
        )}
        {suppliers.map((s) => (
          <MenuItem key={s.supplier_id} value={s.supplier_id}>{s.supplier_name}</MenuItem>
        ))}
      </Select>
      {error !== null && (
        <FormHelperText>{t('common.suppliersLoadFailed')}</FormHelperText>
      )}
    </FormControl>
  );
}
